import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useAuthenticatedApiRequest from "../../AuthenticatedApiRequest";
import TchrURL from "../../TchrURL";

interface Template {
  id: string;
  name: string;
  tags: {
    id: string;
    category: string;
    subcategory?: string;
    tagText: string;
    general: boolean;
  }[];
}

interface UserTemplates {
  userId: string;
  templates: Template[];
}

interface CreateTemplateRequest {
  id: string;
  name: string;
  fileHash: string;
  tags: {
    category: string;
    subcategory?: string;
    tagText: string;
    general: boolean;
  }[];
}

// Get user templates
export const useUserTemplates = (category?: string, options?: { enabled?: boolean }) => {
  const { get } = useAuthenticatedApiRequest();

  return useQuery<UserTemplates>({
    queryKey: ["user", "templates", category],
    enabled: options?.enabled ?? true,
    queryFn: async () => {
      let urlPath = "user/templates";
      if (category) {
        urlPath += `?category=${encodeURIComponent(category)}`;
      }

      const url = new TchrURL(urlPath);
      const response = await get(url, { headersOpts: {} });

      return response.json();
    },
    retry: (failureCount, error) => {
      // Don't retry on 404s
      console.error('template error:', error)
      if (error.message.includes('404')) {
        return false;
      }
      return failureCount < 1;
    },
    throwOnError: true

  });
};



export const useTemplateTags = () => {
  const { get } = useAuthenticatedApiRequest();

  return useQuery<{
    results: {
      category: string;
      subcategory?: string;
      tagText: string;
      general: boolean;
      id: number;
      key: string;
    }[]
  }>({
    queryKey: ["user", "tags"],
    queryFn: async () => {
      const url = new TchrURL("user/tags");
      const response = await get(url);
      if (response.status === 404) {
        throw new Error("404: Template tags not found");
      }

      return response.json();
    },
    retry: (failureCount, error) => {
      if (error.message.includes('404')) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

// Create template
export const useCreateTemplate = () => {
  const { post } = useAuthenticatedApiRequest();

  return useMutation<void, Error, CreateTemplateRequest>({
    mutationKey: ["user", "templates", "create"],
    mutationFn: async (templateData: CreateTemplateRequest) => {
      const url = new TchrURL("user/templates");
      const response = await post(url, JSON.stringify(templateData));

      return response.json();
    },
  });
};

// Delete template  
export const useDeleteTemplate = () => {
  const { del } = useAuthenticatedApiRequest();
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationKey: ["user", "templates", "delete"],
    mutationFn: async (templateId: string) => {
      const url = new TchrURL(`user/templates/${templateId}`);
      const response = await del(url);
      if (response.status === 404) {
        throw new Error("404: Template not found");
      }

      if (response.ok) {
        queryClient.setQueryData<UserTemplates>(["user", "templates"], (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            templates: oldData.templates.filter((template) => template.id !== templateId),
          };
        });
      }

      return response.json();
    },
  });
};
